/**
 * Traffic cameras — "Phlock" (owner's ask, docs/DESIGN_CAMERAS.md). A `traffic_camera` prop (content/props) on the pavement
 * watches CAM_RANGE tiles around it (12 since Sep 23: the path world's roads are wide, and 7 missed the far lanes of an avenue): a player driving past over the road's limit, through a red light at the junction it
 * covers, or committing a crime in its view gets flashed. The flash goes out as an event (the client copies the frame as
 * evidence, photos.ts); LETTER_DELAY game hours later a letter arrives with the fine, taken from the bank, then the pocket,
 * else owed (it comes off the next job). Everything the server needs lives on PlayerState.fines.
 */
import { count as countAch } from './achievements';
import type { Game, PlayerState } from './game';
import { hasLight, lightGreen, lightsOn } from './ai';
import type { JunctionBox, PathRoads } from '../world/path-adapter';
import { maskedFace } from './cosmetics';
import { DEFAULT_LIMIT } from './physics';
import type { Person } from './entities';
import { AIRCRAFT_CLASSES, WATER_CLASSES } from '../content/types';
import type { WorldStream } from '../engine/world-stream';
import { report } from './newsfeed';

export interface Fine {
  id: string;
  kind: 'speed' | 'red' | 'crime';
  /** game time of the offence and when the letter is due */
  at: number; due: number;
  x: number; y: number;
  /** where (the town) */
  place: string;
  amount: number;
  /** speed: km/h measured and the limit */
  speed?: number; limit?: number;
  /** the letter has been delivered (and the money taken or owed) */
  paid?: boolean;
}

export const CAM_RANGE = 12, CAM_COOLDOWN = 25, LETTER_DELAY_H = 3, SPEED_SLACK = 8;
export { DEFAULT_LIMIT };
export const FINES = { speed: (over: number) => 60 + Math.round(over * 4), red: 160, crime: 250 };
const GAME_HOUR = 40; // seconds of real time per game hour (a day is 960 s)

/** Camera tiles per live chunk (built once per chunk). */
const cache = new WeakMap<object, { x: number; y: number }[]>();
/** the standing cameras within r tiles (a square) of (x, y) — the fines' and (Sep 24) the radar detector's, on the client's world too */
export function camerasNear(world: WorldStream, x: number, y: number, r: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (const lc of world.liveChunks()) {
    const c = lc.chunk;
    if (x + r < c.x0 || x - r > c.x0 + 64 || y + r < c.y0 || y - r > c.y0 + 64) continue;
    let list = cache.get(c);
    if (!list) { list = c.props.filter((p) => world.gen.propList[p.prop]?.tags?.includes('traffic_camera')).map((p) => ({ x: p.x + 0.5, y: p.y + 0.5 })); cache.set(c, list); }
    for (const cam of list) if (Math.abs(cam.x - x) <= r && Math.abs(cam.y - y) <= r && !world.broken.has(`${Math.floor(cam.x)},${Math.floor(cam.y)}`)) out.push(cam); // (a mast knocked down watches nothing — owner, Sep 11)
  }
  return out;
}

/** (owner, Sep 24) how far a radar detector hears a camera (tiles) */
export const RADAR_RANGE = 90;
/**
 * (owner, Sep 24: "a radar detector: if you are driving over the speed limit, it beeps to tell how near the Phlock camera is; you
 * could turn it on and off when driving") How near the nearest standing camera is, for a driver over the limit of the road they
 * are on with a detector in the pockets and switched on: 0 at the edge of RADAR_RANGE … 1 inside the camera's own CAM_RANGE.
 * Undefined keeps it quiet — under the limit, no camera in range, not driving on the ground, or no detector.
 */
export function radarLevel(world: WorldStream, p: Person): number | undefined {
  if ((p.items.radar_detector ?? 0) <= 0 || p.radarOff || p.dead) return undefined;
  const v = p.vehicle; if (!v || v.driver !== p || v.wrecked || v.alt >= 0.5 || WATER_CLASSES.has(v.def.class) || AIRCRAFT_CLASSES.has(v.def.class)) return undefined;
  if (Math.abs(v.speed) * 3.6 <= (world.roadAt(v.x, v.y)?.limit ?? DEFAULT_LIMIT)) return undefined;
  let best = Infinity;
  for (const c of camerasNear(world, v.x, v.y, RADAR_RANGE)) best = Math.min(best, Math.hypot(c.x - v.x, c.y - v.y));
  if (best > RADAR_RANGE) return undefined;
  return 1 - Math.max(0, best - CAM_RANGE) / (RADAR_RANGE - CAM_RANGE);
}

/**
 * (owner, Sep 23: "I want the red lights added too") The lit junction a driver at (x, y) heading `axis` is inside, and whether its light
 * is red for that axis now — on the lattice the 16-tile grid's crossings, in the path world (the live one) the junction boxes, whose
 * phases are the ones the traffic obeys and the renderer draws. Undefined outside a lit junction.
 */
const boxScratch: JunctionBox[] = [];
export function litJunction(game: Game, x: number, y: number, axis: 'v' | 'h'): { key: string; red: boolean } | undefined {
  const w = game.world;
  if (w.gen.paths) {
    for (const b of (w.gen.net as PathRoads).junctionBoxes(x - 14, y - 14, x + 14, y + 14, boxScratch)) { // (the query is by the node: a box reaches up to a dozen tiles from its node)
      if (!b.h || !b.v || x < b.x0 || x >= b.x1 + 1 || y < b.y0 || y >= b.y1 + 1) continue;
      if (b.h.road.class === 'rail' || b.v.road.class === 'rail') return undefined;
      const busy = w.busyAt(b.nx, b.ny);
      if (!hasLight(b.v.road.width, b.h.road.width, busy) || !lightsOn(game.hour, b.v.road.width, b.h.road.width, busy)) return undefined;
      return { key: `${b.nx},${b.ny}`, red: !lightGreen(b.v.k, b.h.k, axis, w.clock) };
    }
    return undefined;
  }
  const net = w.gen.net;
  const gx = Math.round(x / 16) * 16, gy = Math.round(y / 16) * 16;
  const lv = net.lineAt('v', gx, gy), lh = net.lineAt('h', gy, gx);
  const busy = w.busyAt(gx, gy);
  if (!lv || !lh || !hasLight(lv.road.width, lh.road.width, busy) || !lightsOn(game.hour, lv.road.width, lh.road.width, busy)) return undefined;
  // inside the junction box (the two roads' widths), not just near it
  if (Math.abs(x - gx) > lv.road.width / 2 + 0.5 || Math.abs(y - gy) > lh.road.width / 2 + 0.5) return undefined;
  return { key: `${gx},${gy}`, red: !lightGreen(gx, gy, axis, w.clock) };
}

/** Every tick for every player: watch the cameras in range, deliver the letters that are due. */
export function stepCameras(game: Game, st: PlayerState, dt: number): void {
  const p = st.person;
  st.fines ??= [];
  st.camCool ??= new Map();
  const wantedBefore = st.camWanted ?? st.wanted; st.camWanted = st.wanted;
  // the junction the car is in, and whether it drove in on red: what the light was the tick it entered (Sep 23)
  let ranRed = false;
  { const dv = p.vehicle && p.vehicle.driver === p && p.vehicle.alt < 0.5 && !p.dead ? p.vehicle : undefined;
    const j = dv && Math.abs(dv.speed) * 3.6 > 8 ? litJunction(game, dv.x, dv.y, Math.abs(Math.sin(dv.angle)) > Math.abs(Math.cos(dv.angle)) ? 'h' : 'v') : undefined;
    if (!j) st.camBox = undefined; else if (st.camBox?.key !== j.key) { st.camBox = { key: j.key, red: j.red }; ranRed = j.red; } }
  if (!p.dead && !p.inside) {
    const cams = camerasNear(game.world, p.x, p.y, CAM_RANGE);
    if (cams.length) {
      const v = p.vehicle && p.vehicle.driver === p && p.vehicle.alt < 0.5 ? p.vehicle : undefined;
      const road = v ? game.world.roadAt(v.x, v.y) : undefined;
      const kmh = v ? Math.abs(v.speed) * 3.6 : 0;
      const limit = road?.limit ?? DEFAULT_LIMIT;
      // who the fine can be sent to (owner): a face the camera sees, or the plate of a car registered to you. A mask (cosmetics tag
      // `mask`) hides the face, so a masked driver in a car that is not theirs is nobody — the flash still goes off; a masked driver
      // in their own car gets the letter by the plate. An emergency vehicle with the siren on is exempt — the camera lets it through.
      const emergency = !!v && !!v.siren && v.def.tags.some((t) => t === 'emergency' || t === 'police');
      const traceable = !game.rules.disguise || !maskedFace(game.reg, p.look) || (!!v && v.owner !== undefined && v.owner === st.token && !v.mods?.plates); // (a world rule: disguises count; stolen plates (a mission's reward) hide an owned car — owner, Q58)
      for (const cam of cams) {
        const key = `${cam.x},${cam.y}`;
        if ((st.camCool.get(key) ?? -1e9) > game.time - CAM_COOLDOWN) continue;
        let kind: Fine['kind'] | undefined;
        if (st.wanted > wantedBefore) kind = 'crime';
        else if (game.rules.speedCameras && v && road && kmh > limit + SPEED_SLACK) kind = 'speed';
        else if (game.rules.redLightCameras && v && (ranRed || (st.camBox?.red && st.camBox.key !== st.camRedKey))) { kind = 'red'; st.camRedKey = st.camBox?.key; }
        if (!kind || emergency) continue;
        if (!traceable) { st.camCool.set(key, game.time); game.events.push({ kind: 'flash', x: cam.x, y: cam.y, cls: `${Math.floor(game.time * 10).toString(36)}-${p.id.toString(36)}`, who: p.id }); game.say('📸 Flash — the camera got a picture, but not your face', p); break; }
        st.camCool.set(key, game.time);
        const id = `${Math.floor(game.time * 10).toString(36)}-${p.id.toString(36)}`;
        const place = game.world.gen.fields.settlements.in(cam.x, cam.y)?.name ?? 'the city';
        const fine: Fine = { id, kind, at: game.time, due: game.time + LETTER_DELAY_H * GAME_HOUR, x: cam.x, y: cam.y, place, amount: kind === 'speed' ? FINES.speed(kmh - limit) : FINES[kind], ...(kind === 'speed' ? { speed: Math.round(kmh), limit } : {}) };
        st.fines.push(fine);
        game.events.push({ kind: 'flash', x: cam.x, y: cam.y, cls: id, who: p.id });
        game.say(`📸 Flash — the camera at ${place} got you${kind === 'speed' ? ` doing ${Math.round(kmh)} in a ${limit}` : kind === 'red' ? ' running the red' : ''}`, p);
        break;
      }
    }
  }
  // the post
  for (const f of st.fines) {
    if (f.paid || game.time < f.due) continue;
    f.paid = true;
    const what = f.kind === 'speed' ? `speeding — ${f.speed} km/h where ${f.limit} is the limit` : f.kind === 'red' ? 'driving through a red light' : 'an offence committed in view of the camera';
    let how: string;
    if (st.bank >= f.amount) { st.bank -= f.amount; how = 'taken from your account'; }
    else if ((p.items.cash ?? 0) >= f.amount) { p.items.cash = (p.items.cash ?? 0) - f.amount; how = 'paid from your pocket'; }
    else { st.owed = (st.owed ?? 0) + f.amount; how = 'unpaid — it comes off your next job'; }
    countAch(game, st, 'fines', f.amount);
    game.say(`✉ A letter: $${f.amount} fine for ${what} at ${f.place} (${how})`, p);
    game.post(st, { id: f.id, title: `${f.place} traffic office — fixed penalty`, text: `Our camera at ${f.place} recorded ${what}.\n\nFixed penalty: $${f.amount}, ${how}.\nThe recording is enclosed. Drive carefully.` });
    if (Math.random() < 0.2) report(game, 'camera', f.x, f.y, { place: f.place, what: f.kind === 'speed' ? `${f.speed} km/h` : f.kind === 'red' ? 'a red light' : 'an offence' }, st.name);
  }
  if (st.fines.length > 40) st.fines.splice(0, st.fines.length - 40);
  void dt;
}

/** What a player still owes is taken off a job's pay (jobs.ts complete()). */
export function settleOwed(st: PlayerState, pay: number): { pay: number; taken: number } {
  const owed = st.owed ?? 0; if (owed <= 0) return { pay, taken: 0 };
  const taken = Math.min(owed, pay); st.owed = owed - taken; if (st.owed <= 0) delete st.owed;
  return { pay: pay - taken, taken };
}
